import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem,
} from '@mui/material';
import { Home, Edit, DirectionsBus, OpenInNew } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;

export default function BusList() {
  const navigate = useNavigate();
  const [buses, setBuses] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(null);
  const { attemptClose, discardDialog } = useUnsavedGuard({
    open: dialogOpen, value: form, onClose: () => setDialogOpen(false),
    message: 'You have unsaved bus profile changes. Close without saving?',
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API}/api/fleet/buses`, { headers }),
        axios.get(`${API}/api/fleet/routes`, { headers }),
        axios.get(`${API}/api/employees`, { headers }).catch(() => ({ data: [] })),
      ]);
      setBuses(a.data); setRoutes(b.data);
      setEmployees(Array.isArray(c.data) ? c.data : (c.data?.employees || [])); setError('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to load buses'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const empName = (no) => {
    const e = employees.find(x => x.employeeNo === no);
    return e ? `${e.firstName || ''} ${e.lastName || ''}`.trim() || no : no;
  };

  const openEdit = (bus) => {
    setForm({
      faNo: bus.faNo, faName: bus.faName,
      capacity: bus.profile?.capacity ?? '',
      driverEmployeeId: bus.profile?.driverEmployeeId || '',
      assistantEmployeeId: bus.profile?.assistantEmployeeId || '',
      routeCode: bus.profile?.routeCode || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.capacity || Number(form.capacity) <= 0) return setError('Capacity must be greater than 0');
    try {
      await axios.put(`${API}/api/fleet/bus-profiles/${form.faNo}`, {
        capacity: Number(form.capacity),
        driverEmployeeId: form.driverEmployeeId,
        assistantEmployeeId: form.assistantEmployeeId,
        routeCode: form.routeCode,
      }, { headers });
      setDialogOpen(false); setError(''); setSuccess('Bus profile saved'); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
  };

  if (loading) return (<Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /></Box>);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center"><DirectionsBus sx={{ mr: 1, color: '#0288d1' }} /><Typography variant="h5" sx={{ color: '#0288d1' }}>Buses</Typography></Box>
            <Box>
              <IconButton onClick={() => navigate('/fleet')} title="Fleet Dashboard" sx={{ mr: 1 }}><Home /></IconButton>
              <Button size="small" startIcon={<OpenInNew />} onClick={() => navigate('/fixed-assets')}>Add a bus (Fixed Assets)</Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Buses are Fixed Assets in subclass <strong>BUS</strong>. Create the bus in Fixed Assets, then set its capacity, driver &amp; route here.
          </Typography>
          <Toast error={error} success={success} onClose={(k) => k === 'error' ? setError('') : setSuccess('')} />
          {buses.length === 0 ? (
            <Alert severity="info">No buses yet. Create a Fixed Asset in subclass <strong>BUS</strong> (FA Class MOTOR-VEH), then it appears here.</Alert>
          ) : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>FA No</strong></TableCell><TableCell><strong>Name</strong></TableCell>
                <TableCell align="right"><strong>Capacity</strong></TableCell><TableCell><strong>Driver</strong></TableCell>
                <TableCell><strong>Assistant</strong></TableCell><TableCell><strong>Route</strong></TableCell>
                <TableCell><strong>Profile</strong></TableCell><TableCell></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {buses.map(b => (
                  <TableRow key={b.faNo} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{b.faNo}</TableCell>
                    <TableCell>{b.faName}</TableCell>
                    <TableCell align="right">{b.profile?.capacity ?? '—'}</TableCell>
                    <TableCell>{b.profile?.driverEmployeeId ? empName(b.profile.driverEmployeeId) : '—'}</TableCell>
                    <TableCell>{b.profile?.assistantEmployeeId ? empName(b.profile.assistantEmployeeId) : '—'}</TableCell>
                    <TableCell>{b.profile?.routeCode || '—'}</TableCell>
                    <TableCell><Chip label={b.profile ? 'Set' : 'Not set'} color={b.profile ? 'success' : 'warning'} size="small" variant="outlined" /></TableCell>
                    <TableCell><IconButton color="primary" size="small" onClick={() => openEdit(b)}><Edit fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>Bus Profile — {form?.faName} ({form?.faNo})</DialogTitle>
        <DialogContent>
          {form && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField label="Capacity (seats)" type="number" value={form.capacity} size="small" fullWidth
                onChange={e => setForm({ ...form, capacity: e.target.value })} inputProps={{ min: 1 }} />
              <TextField select label="Driver" value={form.driverEmployeeId} size="small" fullWidth
                onChange={e => setForm({ ...form, driverEmployeeId: e.target.value })}>
                <MenuItem value="">(none)</MenuItem>
                {employees.map(e => <MenuItem key={e.employeeNo} value={e.employeeNo}>{`${e.firstName || ''} ${e.lastName || ''}`.trim()} ({e.employeeNo})</MenuItem>)}
              </TextField>
              <TextField select label="Assistant" value={form.assistantEmployeeId} size="small" fullWidth
                onChange={e => setForm({ ...form, assistantEmployeeId: e.target.value })}>
                <MenuItem value="">(none)</MenuItem>
                {employees.map(e => <MenuItem key={e.employeeNo} value={e.employeeNo}>{`${e.firstName || ''} ${e.lastName || ''}`.trim()} ({e.employeeNo})</MenuItem>)}
              </TextField>
              <TextField select label="Route" value={form.routeCode} size="small" fullWidth
                onChange={e => setForm({ ...form, routeCode: e.target.value })}>
                <MenuItem value="">(none)</MenuItem>
                {routes.map(r => <MenuItem key={r.routeCode} value={r.routeCode}>{r.routeCode} — {r.name}</MenuItem>)}
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={attemptClose}>Cancel</Button><Button variant="contained" onClick={save}>Save</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
